<template>
  <div class="product-card__container d-flex align-items-center justify-content-start clickable" @click="selectProduct()">
    <div class="product-card__body d-flex justify-content-between align-items-center">
      <div class="product-card__body-left d-flex align-items-center">
        <a :href="hrefUrl" target="_blank">
          <div class="product-card__img">
            <img :src="imgSrc" alt="product-img" loading="lazy">
            <i v-popover:tooltip.top="'View live page'" class="icon icon-view"></i>
          </div>
        </a>
        <div class="product-card__content">
          <div
            v-popover:tooltip.top="title"
            class="product-card__name paragraph-12-medium text-light-high"
            :class="{ 'assign-default': willAssignToDefault }"
            @click="selectProduct()"
          >
            {{ title }}
          </div>
          <div v-if="willAssignToDefault" class="product-card__info d-flex align-items-center" @click="selectProduct()">
            <i class="icon icon-infor-fill mr-1" />
            <span class="body-12-regular text-light-high">Will be assigned to the default page</span>
          </div>
        </div>
      </div>
      <div class="product-card__body-right d-flex align-items-center justify-content-end col-md-2 col-xl-2">
        <div
          v-if="status !== 'Active'"
          v-popover:tooltipProductCard.top="dynamicStatusTooltip"
          class="product-card__status paragraph-12-medium px-half"
          :class="dynamicStatusClass"
          @click="selectProduct()">
          {{ status }}
        </div>
        <div class="product-card__checkbox">
          <label
            v-popover:tooltipProductCard.top="isDisabledSelect ? 'All unassigned products will be assigned to the default page' : undefined"
            class="product-card__checkbox-item"
            :class="[
              { checked: isSelected },
              { 'checked-disabled': isDisabledSelect },
              { 'unchecked-disabled': isSubmitting },
            ]">
            <i
              v-if="product.assignment.selected.includes(info.id) || isDisabledSelect || isSubmitting"
              class="icon icon-selected text-light1"></i>
            <input v-model="selectedProduct" type="checkbox" :value="info.id" @click="selectProduct()" />
          </label>
        </div>
      </div>
    </div>
    <Portal to="tooltips">
      <transition name="fade">
        <Tooltip name="tooltipProductCard" :delay="500" :pointer="true" :width="'fit-content'" :maxWidth="'fit-content'"/>
      </transition>
    </Portal>
  </div>
</template>

<script>
import Checkbox from "@/core/shared/base/Checkbox.vue";
import {mapState, mapMutations} from "vuex";
import { mutations } from "@/store/product/types";
import { replaceImageToSize } from "@/plugins/shopify.js";
/* eslint-disable vue/no-unused-components */
export default {
  name: "ProductCard",
  components: {
    Checkbox
  },
  props: {
    info: Object,
    isAssignedDefault: Boolean,
    pageInfo: Object,
    isSubmitting: Boolean,
  },
  data() {
    return {
      selectedProduct: [],
      makeDefaultPages: ["template-collection", "template-index", "template-product"],
    };
  },
  computed: {
    ...mapState({
      shopInfo: state => state.account.shopInfo,
    }),
    ...mapState("product", { 
      product: "product",
    }),
    currentPageId() {
      return this.pageInfo.page_id || this.pageInfo.pageId;
    },
    layoutDefaultStatus() {
      return this.makeDefaultPages.includes(this.pageInfo.type) ? true : false;
    },
    isMakeDefault () {
      if (!this.layoutDefaultStatus) return false;
      return this.pageInfo.published;
    },
    hrefUrl() {
      return `https://${this.shopInfo.shopifyDomain}/products/${this.info.handle}`;
    },
    dynamicStatusClass() {
      return `product-card__status-${this.info.status.toLowerCase()}`;
    },
    dynamicStatusTooltip() {
      return `This product will not display on your live page`;
    },
    imgSrc() {
      const expectImageSize = '72x';
      const src =  this.info.image
        ? this.info.image?.src
        : 'https://cdn.shopify.com/s/assets/no-image-2048-5e88c1b20e087fb7bbe9a3771824e743c244f437e4f8ba93bbf7b11b53f7824c.gif';
      return replaceImageToSize(src, expectImageSize);
    },
    title() {
      return this.info.title;
    },
    status() {
      return this.capitalizeFirstLetter(this.info?.status.toLowerCase()) || '';
    },
    willAssignToDefault() {
      const pageId = this.currentPageId;
      const unSelectedList = this.product.assignment.unselected;
      const pageAssignedList = this.info && this.info.assigned_page_ids;
      const isOnlyAssignedToCurrentPage = pageAssignedList.length == 1 && pageAssignedList[0] == pageId;
      return (
        (isOnlyAssignedToCurrentPage && unSelectedList.includes(this.info.id) && !this.isMakeDefault) ||
        (unSelectedList.includes(this.info.id) && this.pageInfo.is_published)
      );
    },
    isDisabledSelect() {
      const pageId = this.currentPageId;
      const productAssignedPages = this.info.assigned_page_ids;
      return (
        ( !productAssignedPages 
          || productAssignedPages.length == 0 
          || productAssignedPages.includes(pageId)
        ) && this.isMakeDefault
      );
    },
    isSelected() {
      const pageId = this.currentPageId;
      const isAssignToDefault = this.info.assigned_page_ids.includes(pageId) && this.isMakeDefault;
      return this.product.assignment.selected.includes(this.info.id) && !isAssignToDefault;
    },
    isAssignedToCurrentPage() {
      const pageId = this.currentPageId;
      const pageAssignedList = this.info && this.info.assigned_page_ids;
      return pageAssignedList.includes(pageId);
    },
  },
  mounted() {
    const isAssignedToCurrentPage = this.isAssignedToCurrentPage;
    const isNotBeingUnselect = !this.product.assignment.unselected.includes(this.info.id);

    if (isAssignedToCurrentPage && isNotBeingUnselect && !this.isDisabledSelect) {
      this.setAssignedProduct(this.info.id);
    }
  },
  methods: {
    ...mapMutations("product", {
      setAssignedProduct: mutations.SET_ASSIGNED_PRODUCT,
      updateAssignedProduct: mutations.UPDATE_ASSIGNED_PRODUCT,
    }),
    capitalizeFirstLetter(string) {
      return string?.charAt(0).toUpperCase() + string?.slice(1);
    },
    selectProduct() {
      if(this.isDisabledSelect || this.isSubmitting) return;
      this.updateAssignedProduct(this.info.id);
    }
  }
};
</script>

<style lang="scss" scoped>
  .product-card {
  &__info {
    margin-top: 4px;
    .icon {
      color: $mono5;
    }
  }
  &__status {
    cursor: auto !important;
    border-radius: 3px;
    height: 16px;
    width: wrap-content;
    margin-right: 22.75px;
    line-height: 16px !important;
    &-archived {
      color: $textLightModeHigh;
      background-color: $textDarkModeHigh;
    }
    &-draft {
      color: $accentAmber500;
        background-color: $accentAmber100;
    }
    &-tooltip {
      max-width: 500px !important;
    }
  }
  &__container {
    height: 72px;
    border-radius: 3px;
    background-color: #ffffff;
    border: solid 1px $light3;
    @include trans-all;
    &:not(:last-child) {
      margin-bottom: 14px;
    }
    &:hover {
      border: solid 1px $dark8;
    }
  }
  &__img {
    width: 72px;
    height: 70px;
    background-color: darkgrey;
    border-radius: 3px 0 0 3px;
    position: relative;

    > img {
      width: 100% !important;
      height: 100% !important;
      border-radius: 3px 0 0 3px;
      object-fit: cover;
    }

    .icon-view {
      opacity: 0;
      position: absolute;
      transform: translate(-50%, -50%);
      top: 50%;
      left: 50%;
      color: #ffffff;
      font-size: 20px;
      transition: all 0.3s ease-in-out;
    }

    &::before {
      content: '';
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      background: $dark4;
      border-radius: 3px 0 0 3px;
      opacity: 0;
      transition: all 0.3s ease-in-out;
      cursor: pointer;
    }
  }
  &__body {
    flex-grow: 1;
  }
  &__name {
    line-height: 18px !important;
    max-width: 300px;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
  }
  &__content {
    margin-left: 12px;
  }
}

.product-card__body-right {
  padding: 0 !important;
  margin-right: 16px;
}

.product-card__img:hover {
  .icon-view {
    opacity: 1;
  }
  &::before {
    opacity: 0.5;
  }
}

.assign-default {
  -webkit-line-clamp: 1;
}

.view-live-tooltip {
  max-width: 345px !important;
}
.product-card__checkbox {
  width: 20px;
  height: 20px;
  @include flex-center;
}
.product-card__checkbox-item {
  width: 17.5px;
  height: 17.5px;
  outline: 2px solid transparent;
  background-color: #ffffff;
  border-radius: 3px;
  padding-bottom: 0px !important;
  position: relative;
  @include trans-all;
  @include flex-center;
  input {
    position: absolute;
    opacity: 0;
    cursor: pointer;
    height: 0;
    width: 0;
  }
  .icon {
    height: inherit;
  }
  .icon::before {
    font-size: 10.83px;
  }
  &.checked-disabled {
    background: $neutral7;
    .icon {
      color: $light3;
    }
  }
  &:before {
    content: '';
    background-color: rgba(0, 0, 0, 0.05);
    border-radius: 3px;
    position: absolute;
    width: 28px;
    height: 28px;
    opacity: 0;
    @include trans-all;
  }
  &:not(.checked):not(.checked-disabled):not(.unchecked-disabled):hover {
    background-color: rgba(0, 0, 0, 0.05);
  }
  &:not(.checked-disabled) {
    border: solid 1.25px $dark6;
  }
  &:not(.checked-disabled):not(.unchecked-disabled):hover:before {
    opacity: 1;
    cursor: pointer;
  }

  &.checked {
    background: $mono3;
    border-color: $mono3;
  }
}
</style>
